import React, { useState, useRef, useMemo, useEffect, forwardRef } from 'react';
import Table from 'react-bootstrap/Table';
import { dateFormat } from "../../utils";
import Pagination from 'react-responsive-pagination';
import moment from 'moment-timezone';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'bootstrap/dist/css/bootstrap.min.css';
import './TripTable.css';
import TripPDF from '../pdfContent/TripPDF';
import MapComponent from '../MapComponent';
import { Dialog } from 'primereact/dialog';
import { ToastContainer } from 'react-toastify';
import LegDetailsModal from '../ModalContent/LegDetailsModal';
import { Link } from 'react-router-dom';

/** Leg list from v1 add trip row (`transData` array). Falls back to legacy single `schedule_tran`. */
function getTransData(row) {
    if (Array.isArray(row?.transData)) {
        return [...row.transData].sort(
            (a, b) => (a?.schedule_trans_id ?? 0) - (b?.schedule_trans_id ?? 0)
        );
    }
    if (row?.schedule_tran) return [row.schedule_tran];
    return [];
}

function formatPassengerName(passenger) {
    if (!passenger) return 'N/A';
    const first = passenger.first_name ?? passenger.firstName ?? '';
    const middle = passenger.middle_name ?? passenger.middleName ?? '';
    const last = passenger.last_name ?? passenger.lastName ?? '';
    const name = `${first} ${middle} ${last}`.replace(/\s+/g, ' ').trim();
    const pmi = passenger.passenger?.pass_pmi_no;
    return name ? `${name}${pmi ? ` - ${pmi}` : ''}` : 'N/A';
}

const TripTable = forwardRef(({ 
    tableData, 
    showAllRowsForExport,
    filterPayer,
    filterFromDate,
    filterToDate,
    filterDriver,
    filterPassenger,
    filterTripStatusStr,
    onRefreshTrips
}, ref) => {
    
    const pdfRef = useRef();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const totalPages = Math.ceil(tableData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    const paginatedData = useMemo(() => {
        return tableData.slice(startIndex, endIndex);
    }, [tableData, startIndex, endIndex]);
    
    const [loadingRow, setLoadingRow] = useState(null);
    const [visible, setVisible] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [showLegsModal, setShowLegsModal] = useState(false);
    const [selectedLegsData, setSelectedLegsData] = useState([]);
    const [selectedScheduleId, setSelectedScheduleId] = useState(null);
    const [selectedScheduleDetails, setSelectedScheduleDetails] = useState(null);

    useEffect(() => {
        setCurrentPage(1);
    }, [tableData]);

    const myArray = {};
    paginatedData.forEach((val) => {
        const schid = val.schedule_id;
        const transList = getTransData(val);
        if (!schid || transList.length === 0) return;
        if (!myArray[schid]) {
            myArray[schid] = [];
        }
        transList.forEach((trans) => {
            if (
                !myArray[schid].some(
                    (item) => item.schedule_trans_id === trans.schedule_trans_id
                )
            ) {
                myArray[schid].push(trans);
            }
        });
    });

    const uniquePaginatedData = paginatedData.filter(
        (item, index, self) =>
            index === self.findIndex((t) => t.schedule_id === item.schedule_id)
    );

    const [showModal, setShowModal] = useState(false);
    const [pdfDetails, setPdfDetails] = useState(null);

    const handlePdfClick = (details) => {
        localStorage.setItem('pdfDetails', JSON.stringify(details));
        localStorage.setItem('myArray', JSON.stringify(myArray));
        window.open('/home/pdf-preview', '_blank');
    };

    const handleMapIconClick = (trip) => {
        setSelectedTrip(trip);
        setVisible(true);
    };

    const generatePDF = (details, index) => {
        setLoadingRow(index);
        setPdfDetails(details);
        setTimeout(() => {
            var input = pdfRef.current;
            if (input) {
                html2canvas(input, { useCORS: true, allowTaint: true }).then((canvas) => {
                    const imgData = canvas.toDataURL('image/png');
                    const pdf = new jsPDF('p', 'mm', 'a4');
                    const imgWidth = 210;
                    const pageHeight = 297;
                    const padding = 10;
                    const contentWidth = imgWidth - padding * 2;
                    const imgHeight = (canvas.height * contentWidth) / canvas.width;
                    let heightLeft = imgHeight;
                    let position = 0;

                    pdf.addImage(imgData, 'PNG', padding, position, contentWidth, imgHeight);
                    heightLeft -= pageHeight;

                    while (heightLeft >= 0) {
                        position = heightLeft - imgHeight;
                        pdf.addPage();
                        pdf.addImage(imgData, 'PNG', padding, position, contentWidth, imgHeight);
                        heightLeft -= pageHeight;
                    }
                    pdf.save(`Trip_Details_${details.schedule_id}.pdf`);
                    setLoadingRow(null);
                });
            } else {
                console.error("pdfRef.current is null or undefined");
                setLoadingRow(null);
            }
        }, 1000);
    };

    const renderLegs = (legs, renderContent) => {
        return legs && legs.length > 0 ? (
            legs
                .flat()
                .filter(
                    (leg, index, self) =>
                        index ===
                        self.findIndex(
                            (l) =>
                                l.trip?.pickup_location === leg.trip?.pickup_location &&
                                l.trip?.dropOffLocation === leg.trip?.dropOffLocation &&
                                l.trip?.pick_up_time === leg.trip?.pick_up_time &&
                                l.trip?.drop_off_time === leg.trip?.drop_off_time &&
                                l.trip?.startOdometer === leg.trip?.startOdometer &&
                                l.trip?.endOdometer === leg.trip?.endOdometer &&
                                l.trip?.miles === leg.trip?.miles || (
                                    l.trip?.miles != null &&
                                    leg.trip?.miles != null &&
                                    l.trip?.miles === leg.trip?.miles
                                )
                        )
                )
                .map((leg, legIndex) => (
                    <div key={legIndex}>
                        {renderContent(leg, legIndex)}
                    </div>
                ))
        ) : (
            <div>No data available</div>
        );
    };

    const handleShowLegs = (scheduleId, legs, scheduleDetails) => {
        setSelectedScheduleId(scheduleId);
        setSelectedLegsData(legs || []);
        setSelectedScheduleDetails(scheduleDetails);
        setShowLegsModal(true);
    }

    const displayFromDate = filterFromDate ? moment(filterFromDate).format('MM/DD/YYYY') : '';
    const displayToDate = filterToDate ? moment(filterToDate).format('MM/DD/YYYY') : '';
    const displayPayer = filterPayer === '0' ? 'All' : filterPayer;

    const formatDateForScheduleQuery = (dateValue) => {
        if (dateValue == null || String(dateValue).trim() === '') return '';
        const parsed = moment(dateValue, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
    };

    const buildScheduleListPath = useMemo(() => {
        return (scheduleId) => {
            const params = new URLSearchParams();
            if (scheduleId != null && String(scheduleId).trim() !== '') {
                params.set('selectedScheduleId', String(scheduleId).trim());
            }
            const from = formatDateForScheduleQuery(filterFromDate);
            const end = formatDateForScheduleQuery(filterToDate);
            if (from) params.set('from', from);
            if (end) params.set('end', end);
            const queryString = params.toString();
            return queryString
                ? `/home/schedule/list/v1?${queryString}`
                : '/home/schedule/list/v1';
        };
    }, [filterFromDate, filterToDate]);
    return (
        <>
            <div ref={ref} id="trip_table">
                <Table striped bordered hover className='tbl-require' id="trip-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Schedule Date</th>
                            {/* <th>Driver</th> */}
                            <th>Passenger</th>
                            <th>Payor</th>
                            <th style={{ textAlign: 'center' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData && paginatedData.length > 0 ? (
                            paginatedData.map((details, index) => {
                                const legs =
                                    myArray[details.schedule_id]?.length > 0
                                        ? myArray[details.schedule_id]
                                        : getTransData(details);
                                return (
                                    <tr key={details.schedule_id}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{dateFormat(details?.schedule_date) ?? ''}</td>
                                        {/* <td>
                                            {details.driver ?
                                                (details.driver?.firstName && details.driver?.lastName)
                                                    ? (details.driver.firstName + ' ' + (details.driver?.middleName ?? '') + ' ' + details.driver.lastName)
                                                    : details.driver?.fullName || ''
                                                : 'N/A'
                                            }
                                        </td> */}
                                        <td>{formatPassengerName(details.passenger)}</td>
                                        <td>{details.passenger?.passenger?.payerDetails?.payer_name || 'N/A'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {details?.schedule_id != null &&
                                            String(details.schedule_id).trim() !== '' ? (
                                                <Link
                                                    className='btn btn-sm link-button'
                                                    style={{
                                                        backgroundColor: 'rgb(249, 174, 59)',
                                                        borderColor: '#007bff',
                                                        color: '#fff',
                                                        fontWeight: '500',
                                                        marginRight: '6px',
                                                    }}
                                                    title="View Schedule"
                                                    to={buildScheduleListPath(details.schedule_id)}
                                                >
                                                    View Schedule
                                                </Link>
                                            ) : null}
                                            <button
                                                className='btn btn-sm'
                                                style={{ 
                                                    backgroundColor: '#082f49', 
                                                    borderColor: '#007bff', 
                                                    color: 'white',
                                                    fontWeight: '500'
                                                }}
                                                onClick={() => handleShowLegs(details.schedule_id, legs, details)}
                                                title="Add Trip"
                                            >
                                                Add Trip
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="7" style={{ textAlign: 'center' }}>
                                    No trips found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                {paginatedData.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                            <span>Showing {startIndex + 1} to {Math.min(endIndex, tableData.length)} of {tableData.length} entries</span>
                        </div>
                        <Pagination
                            current={currentPage}
                            total={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </div>

            {pdfDetails && (
                <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                    <div ref={pdfRef}>
                        <TripPDF
                            details={pdfDetails}
                            myArray={myArray}
                            filterPayer={displayPayer}
                            filterFromDate={displayFromDate}
                            filterToDate={displayToDate}
                            filterDriver={filterDriver}
                            filterPassenger={filterPassenger}
                            filterTripStatusStr={filterTripStatusStr}
                        />
                    </div>
                </div>
            )}

            <Dialog
                visible={visible}
                onHide={() => setVisible(false)}
                header="Trip Map"
                style={{ width: '80vw', height: '80vh' }}
                modal
            >
                {selectedTrip && (
                    <MapComponent
                        pickupLocation={selectedTrip.trip?.pickup_location}
                        dropoffLocation={selectedTrip.trip?.dropOffLocation}
                    />
                )}
            </Dialog>

            {/* Legs Details Modal */}
            <LegDetailsModal
                visible={showLegsModal}
                onHide={() => setShowLegsModal(false)}
                scheduleId={selectedScheduleId}
                legsData={selectedLegsData}
                scheduleDetails={selectedScheduleDetails}
                onTripsAdded={() => {
                    if (typeof onRefreshTrips === 'function') {
                        onRefreshTrips();
                    }
                }}
            />

            <ToastContainer />
        </>
    );
});

TripTable.displayName = 'TripTable';

export default TripTable;
